import { useState, useRef, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  Sparkles,
  FileSpreadsheet,
  Zap,
  ArrowRight,
} from "lucide-react";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { useTheme } from "../contexts/theme-provider";
import { useAuth } from "../contexts/auth-context";
import { handleProcessPdfFile } from "../services/reports";
import { getAllCurrencyConvRate } from "../services/lookup";

type FileUploadAreaProps = {
  onFileReady: (file: File, isValid: boolean) => void;
  onStartAnalysis: (e: React.FormEvent) => void;
  sector: string;
  subSector: string;
  sectorId: string;
  subSectorId: string;
  reportName: string;
  setReportName: (name: string) => void;
  otherInfo: string;
  setOtherInfo: (info: string) => void;
  isProcessing: boolean;
  error: string | null;
  fileValidationStatus: "none" | "valid" | "invalid";
  setFileId: (fileId: number | null) => void;
  setPdfData: (data: object | null) => void;
  setAuditorOpinion?: (data: object | null) => void;
  localCurrency?: string;
  conversionRate?: number;
  reportCurrency?: string;
  countryCode?: string;
};

export function FileUploadArea({
  onFileReady,
  onStartAnalysis,
  setFileId,
  setPdfData,
  setAuditorOpinion,
  sector,
  subSector,
  sectorId,
  subSectorId,
  reportName,
  isProcessing,
  error,
  fileValidationStatus,
  localCurrency,
  conversionRate,
  reportCurrency,
  countryCode,
}: FileUploadAreaProps) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [isValidating, setIsValidating] = useState(false);
  const [sheetData, setSheetData] = useState<any[][] | null>(null);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [isPdf, setIsPdf] = useState<boolean>(false);
  const [isPdfProcessing, setIsPdfProcessing] = useState<string>("pending");
  const [pdfPreview, setPdfPreview] = useState<any>(null);
  const [auditorOpinionData, setAuditorOpinionData] = useState<any>(null);
  const [pdfValid, setPdfValid] = useState<boolean>(false);
  const [startAnalysisDisabled, setStartAnalysisDisabled] =
    useState<boolean>(false);

  useEffect(() => {
    if (uploadedFile) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, [uploadedFile]);

  const requiredSheets = [
    "Key Financials",
    "Balance Sheet",
    "Income Statement",
    "Ratios",
    "Off Balance Sheet",
  ];

  // ─── Currency / Unit detection & conversion helpers ─────────────────────

  // Scans the first 5 rows of every sheet for a cell matching the pattern
  // "[CURRENCY_CODE] [optional 'in'] [Millions/Thousands/Billions]"
  // e.g. "PKR Millions", "BDT in Million", "USD Billion"
  const detectCurrencyAndUnit = (workbook: XLSX.WorkBook): { currency: string | null; unit: string | null } => {
    const currencyPattern = /([A-Z]{3})\s*(?:in\s*)?(Millions?|Thousands?|Billions?)/i;
    for (const sheetName of workbook.SheetNames) {
      const ws = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });
      for (let i = 0; i < Math.min(5, rows.length); i++) {
        for (const cell of rows[i]) {
          if (typeof cell === "string") {
            const match = cell.match(currencyPattern);
            if (match) {
              const currency = match[1].toUpperCase();
              const unitRaw = match[2].toLowerCase();
              // Normalize to singular form
              const unit = unitRaw.endsWith("s") ? unitRaw.slice(0, -1) : unitRaw;
              return { currency, unit };
            }
          }
        }
      }
    }
    return { currency: null, unit: null };
  };

  // Unit conversion multiplier: converts a value from `fromUnit` to `million`
  const unitToMillionMultiplier = (fromUnit: string): number => {
    const u = fromUnit.toLowerCase();
    if (u === "thousand") return 0.001;
    if (u === "million") return 1;
    if (u === "billion") return 1000;
    return 1;
  };

  // Checks if a cell address is numeric (not a header/label cell)
  // We convert all numeric cells in all sheets, preserving strings/labels.
  const convertWorkbookToMillions = (
    workbook: XLSX.WorkBook,
    fromUnit: string,
  ): XLSX.WorkBook => {
    const multiplier = unitToMillionMultiplier(fromUnit);
    if (multiplier === 1) return workbook; // already in millions

    for (const sheetName of workbook.SheetNames) {
      const ws = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[addr];
          if (cell && typeof cell.v === "number" && !cell.f) {
            // Only convert numeric values, skip formula cells
            cell.v = cell.v * multiplier;
            if (cell.w) {
              // Re-format the display value
              cell.w = cell.v.toLocaleString("en-US", { maximumFractionDigits: 2 });
            }
          }
        }
      }
    }

    return workbook;
  };

  // Converts all numeric cells in the workbook by multiplying with the given FX rate.
  // Rate is "local currency per 1 unit of source currency" (e.g., 280 PKR per USD).
  const convertWorkbookCurrency = (
    workbook: XLSX.WorkBook,
    rate: number,
  ): XLSX.WorkBook => {
    for (const sheetName of workbook.SheetNames) {
      const ws = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[addr];
          if (cell && typeof cell.v === "number" && !cell.f) {
            cell.v = cell.v * rate;
            if (cell.w) {
              cell.w = cell.v.toLocaleString("en-US", { maximumFractionDigits: 2 });
            }
          }
        }
      }
    }
    return workbook;
  };

  // Updates the currency/unit label cells (e.g. "USD Millions" -> "PKR Millions")
  // after conversion so the backend sees consistent headers
  const updateUnitLabels = (
    workbook: XLSX.WorkBook,
    targetUnit: string,
    targetCurrency?: string,
  ): void => {
    const labelPattern = /([A-Z]{3})\s*(?:in\s*)?(Millions?|Thousands?|Billions?)/i;
    const targetLabel = `${targetUnit.charAt(0).toUpperCase() + targetUnit.slice(1)}s`;

    for (const sheetName of workbook.SheetNames) {
      const ws = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");

      for (let R = range.s.r; R <= Math.min(range.s.r + 4, range.e.r); R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[addr];
          if (cell && typeof cell.v === "string" && labelPattern.test(cell.v)) {
            const currencyMatch = cell.v.match(/([A-Z]{3})/i);
            if (currencyMatch) {
              const curr = targetCurrency || currencyMatch[1].toUpperCase();
              cell.v = `${curr.toUpperCase()} ${targetLabel}`;
              cell.w = cell.v;
            }
          }
        }
      }
    }
  };

  // Main function: reads the file, detects currency/unit, converts to millions,
  // and returns a new File object with the converted data.
  const normalizeExcelFile = async (file: File): Promise<File> => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    const { currency: detectedCurrency, unit: detectedUnit } = detectCurrencyAndUnit(workbook);

    // If no unit detected, assume millions (per frontend requirements)
    const sourceUnit = detectedUnit || "million";

    // Determine if currency conversion is needed
    // Rate is "local currency per 1 unit of source currency" (e.g., 280 PKR per USD)
    let effectiveRate = conversionRate;

    // If Excel currency differs from local currency but no rate was provided,
    // auto-fetch the latest conversion rate from the API
    if (
      detectedCurrency &&
      localCurrency &&
      detectedCurrency !== localCurrency &&
      (!effectiveRate || effectiveRate <= 0) &&
      user?.access_token &&
      countryCode
    ) {
      console.log(`[Excel Normalization] No conversion rate provided. Auto-fetching latest rate for ${detectedCurrency} -> ${localCurrency}...`);
      try {
        const rates = await getAllCurrencyConvRate(
          user.access_token,
          countryCode,
          detectedCurrency,
        );
        if (rates && rates.length > 0) {
          // rates are sorted by date desc (latest first) — pick the first
          effectiveRate = parseFloat(rates[0].rate);
          console.log(`[Excel Normalization] Auto-fetched rate: ${effectiveRate} (date: ${rates[0].conversion_date})`);
        } else {
          console.warn(`[Excel Normalization] No conversion rates found for ${detectedCurrency} -> ${localCurrency}`);
        }
      } catch (err) {
        console.error(`[Excel Normalization] Failed to auto-fetch conversion rate:`, err);
      }
    }

    const needsCurrencyConversion =
      detectedCurrency &&
      localCurrency &&
      detectedCurrency !== localCurrency &&
      effectiveRate &&
      effectiveRate > 0;

    // Log original data (first sheet, first 6 rows) before conversion
    const firstSheetName = workbook.SheetNames[0];
    const originalRows = XLSX.utils.sheet_to_json<any[]>(workbook.Sheets[firstSheetName], { header: 1 });
    console.log("[Excel Normalization] Original file:", file.name);
    console.log("[Excel Normalization] Detected currency:", detectedCurrency, "| Detected unit:", detectedUnit);
    console.log("[Excel Normalization] Source unit (after fallback):", sourceUnit, "| Local currency:", localCurrency);
    console.log("[Excel Normalization] Conversion rate:", effectiveRate, "(provided:", conversionRate, "| auto-fetched:", effectiveRate !== conversionRate, ")| Report currency:", reportCurrency);
    console.log("[Excel Normalization] Needs currency conversion:", needsCurrencyConversion);
    console.log("[Excel Normalization] Original data (first sheet, first 6 rows):");
    originalRows.slice(0, 6).forEach((row, i) => console.log(`  Row ${i}:`, JSON.stringify(row)));

    // Step 1: Convert all numeric values to millions
    let converted = convertWorkbookToMillions(workbook, sourceUnit);

    // Step 2: Convert currency to local currency if needed
    if (needsCurrencyConversion) {
      converted = convertWorkbookCurrency(converted, effectiveRate!);
      console.log(`[Excel Normalization] Currency converted: ${detectedCurrency} -> ${localCurrency} (rate: ${effectiveRate})`);
    }

    // Update header labels to reflect the new unit and currency
    if (sourceUnit !== "million" || needsCurrencyConversion) {
      updateUnitLabels(converted, "million", needsCurrencyConversion ? localCurrency : undefined);
    }

    // Log converted data (first sheet, first 6 rows) after conversion
    const convertedRows = XLSX.utils.sheet_to_json<any[]>(converted.Sheets[firstSheetName], { header: 1 });
    console.log("[Excel Normalization] Converted data (first sheet, first 6 rows):");
    convertedRows.slice(0, 6).forEach((row, i) => console.log(`  Row ${i}:`, JSON.stringify(row)));
    const conversions: string[] = [];
    if (sourceUnit !== "million") conversions.push(`${sourceUnit} -> million`);
    if (needsCurrencyConversion) conversions.push(`${detectedCurrency} -> ${localCurrency}`);
    console.log("[Excel Normalization] Conversions applied:", conversions.length ? conversions.join(", ") : "no change needed");

    // Write the converted workbook back to a new File
    const outArrayBuffer = XLSX.write(converted, {
      bookType: "xlsx",
      type: "array",
    });

    const convertedFile = new File([outArrayBuffer], file.name, {
      type: file.type,
    });

    console.log("[Excel Normalization] Final file being sent to backend:", {
      name: convertedFile.name,
      size: convertedFile.size,
      type: convertedFile.type,
    });

    return convertedFile;
  };

  // FI bank-type subsectors share the same Excel template/validation
  const isFiBankSubsector =
    sector === "FI" &&
    (subSector === "Commercial Banks" ||
      subSector === "Development Financial Institutions");

  // --- PDF preview table (FI only) ---
  // These fields are still extracted and sent to the backend — hidden from
  // the editable preview table only.
  const FI_HIDDEN_PREVIEW_FIELDS = new Set([
    "equity investment in associate",
    "risk weighted assets/ total assets",
    "risk weighted assets/total assets",
    "statement unit",
  ]);
  // Non-interest income must sit immediately before "net income before".
  const FI_NON_INTEREST_INCOME = new Set([
    "other interest income",
    "total non-interest income",
    "total non interest income",
    "non-interest income",
  ]);
  const normMetric = (s: string) =>
    s.replaceAll("_", " ").replace(/\s+/g, " ").trim().toLowerCase();
  const PERIOD_MONTHS: Record<string, number> = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
    jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  };
  // Period labels like "Dec 2025", "Jun 2025", "2024" -> sortable [year, month]
  const periodSortKey = (label: string): [number, number] => {
    const m = label.match(/([A-Za-z]{3,})?\s*[-–]?\s*(\d{4})/);
    if (!m) return [0, 0];
    const month = m[1] ? (PERIOD_MONTHS[m[1].slice(0, 3).toLowerCase()] ?? 0) : 0;
    return [parseInt(m[2], 10), month];
  };

  const validateExcelFile = async (file: File): Promise<boolean> => {
    setIsValidating(true);
    setValidationError("");
    try {
      const validExtensions = [".xlsx"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (!validExtensions.includes(fileExtension)) {
        setValidationError(
          "Invalid file format. Please upload an Excel file (.xlsx only)",
        );
        return false;
      }

      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!validMimeTypes.includes(file.type) && file.type !== "") {
        if (file.type !== "") {
          setValidationError(
            "Invalid file type. Please upload a valid Excel file (.xlsx)",
          );
          return false;
        }
      }

      // const maxSize = 10 * 1024 * 1024;
      // if (file.size > maxSize) {
      //   setValidationError(
      //     "File size exceeds 10MB limit. Please upload a smaller file",
      //   );
      //   return false;
      // }

      if (file.size < 1024) {
        setValidationError(
          "File appears to be empty or corrupted. Please upload a valid Excel file",
        );
        return false;
      }

      const isExcelFile = await new Promise<boolean>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer.slice(0, 4));

          const isXLSX = bytes[0] === 0x50 && bytes[1] === 0x4b;

          if (!isXLSX) {
            setValidationError(
              "File does not appear to be a valid Excel file (.xlsx). Please check your file and try again",
            );
            resolve(false);
          } else {
            resolve(true);
          }
        };
        reader.onerror = () => {
          setValidationError(
            "Unable to read the file. Please try uploading again",
          );
          resolve(false);
        };
        reader.readAsArrayBuffer(file.slice(0, 1024));
      });

      if (!isExcelFile) {
        return false;
      }

      if (isFiBankSubsector) {
        const hasRequiredSheets = await new Promise<boolean>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const data = e.target?.result;
              if (!data) {
                setValidationError(
                  "Unable to read file content for validation",
                );
                resolve(false);
                return;
              }

              const workbook = XLSX.read(data, { type: "array" });
              const sheetNames = workbook.SheetNames;

              const missingSheets = requiredSheets.filter(
                (sheet) => !sheetNames.includes(sheet),
              );

              if (missingSheets.length > 0) {
                setValidationError(
                  `Missing required sheets: ${missingSheets.join(", ")}. The file must contain all required financial statements.`,
                );
                resolve(false);
              } else {
                resolve(true);
              }
            } catch (error) {
              setValidationError(
                "Error validating file structure. Please ensure it's a valid Excel file.",
              );
              resolve(false);
            }
          };
          reader.onerror = () => {
            setValidationError("Unable to read the file for validation");
            resolve(false);
          };
          reader.readAsArrayBuffer(file);
        });

        return hasRequiredSheets;
      }

      return true;
    } catch {
      setValidationError(
        "An error occurred while validating the file. Please try again",
      );
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;
      try {
        const workbook = XLSX.read(data, { type: "array" });
        const wsname = workbook.SheetNames[0];
        const ws = workbook.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
        setSheetData(jsonData);
      } catch (error) {
        setValidationError("Error reading the Excel file for preview.");
      }
    };
    reader.onerror = () => {
      setValidationError("Error reading the Excel file for preview.");
    };
    reader.readAsArrayBuffer(file);
  };

  function transformPreview(data: any) {
    const result: any = {};

    for (const year in data) {
      result[year] = {};
      data[year].forEach((item: any) => {
        result[year][item.field] = item.value;
      });
    }

    return result;
  }

  const processPdf = async (file: File) => {
    setIsPdfProcessing("processing");
    const uid = Date.now().toString();
    const finalName = user!.id + "_" + uid + "_" + file.name;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", user!.id);
    formData.append("name", finalName);
    formData.append("sector", sectorId);
    formData.append("sub_sector", subSectorId);


    try {
      const apiResponse = await handleProcessPdfFile(
        formData,
        user!.access_token!,
      );
  
      const transformedPrev = transformPreview(apiResponse?.data)
      
      setPdfPreview(transformedPrev);
      setAuditorOpinionData(apiResponse?.auditor_opinion || null);
      setFileId(apiResponse?.file_id);
      setIsPdfProcessing("processed");
    } catch (error) {
      alert(error)
      setIsPdfProcessing("pending");
      removeFile()
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any[]) => {
      setValidationError("");
      setSheetData(null);
      if (rejectedFiles.length > 0) {
        setValidationError("Please upload only Excel files (.xlsx)");
        onFileReady(acceptedFiles[0], false);
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        if (file.type === "application/pdf") {
          // ✅ For PDF, skip Excel validation
          setIsPdf(true);
          setUploadedFile(file);
          processPdf(file);
          setShowAdditionalInfo(true);
          onFileReady(file, true);
        } else if (
          file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          setIsPdf(false);
          // ✅ Only run Excel validation for Excel files
          const isValid = await validateExcelFile(file);
          if (isValid) {
            // Normalize the Excel: detect unit/currency from headers and
            // convert all numeric values to millions before sending to backend.
            // UI shows the ORIGINAL file; the converted file is sent to backend.
            const normalizedFile = await normalizeExcelFile(file);
            setUploadedFile(file);
            parseExcelFile(file);
            setShowAdditionalInfo(true);
            onFileReady(normalizedFile, true);
          } else {
            onFileReady(file, false);
          }
        } else {
          setValidationError("Unsupported file type");
          onFileReady(file, false);
        }
      }
    },
    [sector, subSector, onFileReady],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
    multiple: false,
    disabled: isValidating,
  });

  const removeFile = () => {
    console.log("remove file triggered.");
    setUploadedFile(null);
    setValidationError("");
    setSheetData(null);
    setShowAdditionalInfo(false);
    onFileReady(null as any, false);

    setPdfData(null);
    setPdfPreview(null);
    setAuditorOpinionData(null);
    if (setAuditorOpinion) setAuditorOpinion(null);

    setIsPdf(false);
    setIsPdfProcessing("pending");
    setPdfPreview(null);
    setPdfValid(false);
    setStartAnalysisDisabled(false);
  };

  const getBorderColor = () => {
    if (fileValidationStatus === "valid") {
      return theme === "dark" ? "!border-green-500" : "!border-green-400";
    } else if (fileValidationStatus === "invalid") {
      return theme === "dark" ? "!border-red-500" : "!border-red-400";
    } else if (isDragActive) {
      return theme === "dark" ? "!border-blue-400" : "!border-blue-400";
    } else {
      return theme === "dark" ? "!border-slate-600" : "!border-slate-300 dark:!border-slate-600";
    }
  };

  const getBackgroundColor = () => {
    if (fileValidationStatus === "valid") {
      return theme === "dark"
        ? "!bg-gradient-to-br !from-green-950/50 !to-emerald-950/50 !shadow-2xl !shadow-green-500/20"
        : "!bg-gradient-to-br !from-green-50 dark:!from-green-900/30 !to-emerald-50 dark:!to-emerald-900/30 !shadow-2xl !shadow-green-500/20";
    } else if (fileValidationStatus === "invalid") {
      return theme === "dark"
        ? "!bg-gradient-to-br !from-red-950/50 !to-orange-950/50 !shadow-2xl !shadow-red-500/20"
        : "!bg-gradient-to-br !from-red-50 dark:!from-red-900/30 !to-orange-50 dark:!to-orange-900/30 !shadow-2xl !shadow-red-500/20";
    } else if (isDragActive) {
      return theme === "dark"
        ? "!bg-gradient-to-br !from-blue-950/50 !to-purple-950/50 !shadow-2xl !shadow-blue-400/30"
        : "!bg-gradient-to-br !from-blue-50 dark:!from-blue-900/30 !to-purple-50 dark:!to-purple-900/30 !shadow-2xl !shadow-blue-500/30";
    } else {
      return theme === "dark" ? "!bg-slate-800/50" : "!bg-white/70 dark:!bg-slate-900/70";
    }
  };

  const Table = ({ data }: { data: any }) => {
    const [tableData, setTableData] = useState(data);
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    // Sync data if prop changes
    useEffect(() => {
      setTableData(normalizeData(data));
    }, [data]);

    // Sync horizontal scroll between header and body
    const handleBodyScroll = () => {
      if (headerRef.current && bodyRef.current) {
        headerRef.current.scrollLeft = bodyRef.current.scrollLeft;
      }
    };

    const normalizeData = (data: any) => {
      const normalized: any = {};

      Object.keys(data).forEach((year) => {
        normalized[year] = {};

        Object.keys(data[year]).forEach((key) => {
          const value = data[year][key];

          normalized[year][key] =
            value === null || value === undefined ? "" : String(value);
        });
      });

      return normalized;
    };

    if (!tableData) return null;

    // Latest period column first (supports "Dec 2025" / "Jun 2025" / "2024")
    const years = Object.keys(tableData).sort((a, b) => {
      const [ya, ma] = periodSortKey(a);
      const [yb, mb] = periodSortKey(b);
      return yb - ya || mb - ma;
    });
    const metrics = Object.keys(tableData[years[0]] || {});

    // Display-only adjustments for FI previews: hide non-report fields and
    // place non-interest income just above net income before tax.
    let displayMetrics = metrics.slice(1);
    if (sector === "FI") {
      displayMetrics = displayMetrics.filter(
        (m) => !FI_HIDDEN_PREVIEW_FIELDS.has(normMetric(m))
      );
      const niiIdx = displayMetrics.findIndex((m) =>
        FI_NON_INTEREST_INCOME.has(normMetric(m))
      );
      if (niiIdx > -1) {
        const [nii] = displayMetrics.splice(niiIdx, 1);
        const nibIdx = displayMetrics.findIndex(
          (m) => normMetric(m) === "net income before"
        );
        displayMetrics.splice(nibIdx === -1 ? 0 : nibIdx, 0, nii);
      }
    }

    const handleChange = (year: string, metric: string, value: string) => {
      const updated = {
        ...tableData,
        [year]: {
          ...tableData[year],
          [metric]: value,
        },
      };
      setTableData(updated);
    };

    const handleSync = () => {
      setPdfPreview(tableData);
    };

    return (
      <div
        className={`overflow-hidden border rounded-lg shadow-md ${
          theme === "dark" ? "border-gray-700" : "border-gray-300"
        }`}
      >
        {/* Header — overflow hidden so scrollbar is hidden, scrollLeft driven by body */}
        <div ref={headerRef} className="overflow-x-hidden">
          <table className="min-w-full border-collapse text-sm table-fixed">
            <thead>
              <tr>
                <th
                  className={`px-3 py-2 border text-left w-64 ${
                    theme === "dark"
                      ? "bg-gray-800 border-gray-700 text-gray-100"
                      : "bg-gray-100 border-gray-200 text-gray-900 dark:text-white"
                  }`}
                >
                  Metric
                </th>
                {years.map((y) => (
                  <th
                    key={y}
                    className={`px-3 py-2 border text-left w-32 ${
                      theme === "dark"
                        ? "bg-gray-800 border-gray-700 text-gray-100"
                        : "bg-gray-100 border-gray-200 text-gray-900 dark:text-white"
                    }`}
                  >
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* Body — scrollable both ways, drives header scroll */}
        <div
          ref={bodyRef}
          onScroll={handleBodyScroll}
          className="overflow-x-auto overflow-y-auto max-h-[60vh]"
        >
          <table className="min-w-full border-collapse text-sm table-fixed">
            <tbody>
              {displayMetrics.map((metric, rowIndex) => (
                <tr
                  key={metric}
                  className={
                    rowIndex % 2 === 0
                      ? theme === "dark"
                        ? "bg-gray-800"
                        : "bg-gray-50 dark:bg-slate-900"
                      : ""
                  }
                >
                  <td
                    className={`px-3 py-1.5 border w-64 font-semibold text-sm ${
                      theme === "dark"
                        ? "border-gray-700 text-gray-200"
                        : "border-gray-200 text-gray-800 dark:text-slate-100"
                    }`}
                  >
                    {metric.replaceAll("_", " ")}
                  </td>
                  {years.map((year) => (
                    <td
                      key={year}
                      className={`px-2 py-1 border w-32 ${
                        theme === "dark" ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <input
                        type="text"
                        value={tableData[year]?.[metric] ?? ""}
                        onChange={(e) =>
                          handleChange(year, metric, e.target.value)
                        }
                        onBlur={() => handleSync()}
                        className={`w-full px-2 py-1 rounded-md text-sm outline-none border transition-colors focus:ring-2 ${
                          theme === "dark"
                            ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500/30"
                            : "bg-white border-gray-300 text-gray-900 dark:text-white focus:border-indigo-400 focus:ring-indigo-400/30"
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isPdf && pdfValid) {
      setStartAnalysisDisabled(false);
    }
  }, [isPdf, pdfValid]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="!space-y-6"
    >
      <motion.div
        whileHover={{ scale: 1.01, y: -2 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          className={cn(
            "!border-2 !border-dashed !transition-all !duration-300 !relative !overflow-hidden !shadow-2xl",
            `${getBorderColor()} ${getBackgroundColor()} !backdrop-blur-sm`,
            isValidating && "!opacity-50 !pointer-events-none",
          )}
        >
          <div className="!absolute !inset-0 !bg-gradient-to-r !from-blue-500/5 !via-purple-500/5 !to-pink-500/5 !opacity-0 hover:!opacity-100 !transition-opacity !duration-500" />

          {!uploadedFile && (
            <>
              <motion.div
                className="!absolute !top-10 !right-10 !w-20 !h-20 !bg-blue-500/10 !rounded-full !blur-2xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="!absolute !bottom-10 !left-10 !w-32 !h-32 !bg-purple-500/10 !rounded-full !blur-2xl"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
              />
            </>
          )}

          <CardContent className="!p-8 !relative !z-10">
            <div
              {...getRootProps()}
              className={cn(
                "!cursor-pointer !text-center !space-y-6",
                isDragActive && "!opacity-50",
              )}
            >
              <input {...getInputProps()} />

              <AnimatePresence mode="wait">
                {uploadedFile ? (
                  <motion.div
                    key="uploaded"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.5, type: "spring" }}
                    className="!space-y-6"
                  >
                    <motion.div
                      initial={{ y: 10 }}
                      animate={{ y: 0 }}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                        fileValidationStatus === "valid"
                          ? theme === "dark"
                            ? "bg-green-950/30 border-green-700/40 text-green-300"
                            : "bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                          : theme === "dark"
                            ? "bg-red-950/30 border-red-700/40 text-red-300"
                            : "bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300"
                      }`}
                    >
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="flex-shrink-0"
                      >
                        <div
                          className={`p-2 rounded-md ${
                            fileValidationStatus === "valid"
                              ? theme === "dark"
                                ? "bg-green-900/40"
                                : "bg-green-100 dark:bg-green-900/30"
                              : theme === "dark"
                                ? "bg-red-900/40"
                                : "bg-red-100 dark:bg-red-900/30"
                          }`}
                        >
                          {fileValidationStatus === "valid" ? (
                            <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                          )}
                        </div>
                      </motion.div>

                      <div className="flex-1 min-w-0 text-left space-y-1">
                        <p className="font-semibold text-sm truncate">
                          {uploadedFile.name}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap text-xs font-medium">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
                              fileValidationStatus === "valid"
                                ? theme === "dark"
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                : theme === "dark"
                                  ? "bg-red-900/30 text-red-300"
                                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            }`}
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
                              theme === "dark"
                                ? "bg-blue-900/30 text-blue-300"
                                : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            }`}
                          >
                            Excel File
                          </span>
                          {reportName && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
                                theme === "dark"
                                  ? "bg-purple-900/30 text-purple-300"
                                  : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                              }`}
                            >
                              Report: {reportName}
                            </span>
                          )}
                        </div>
                        {isFiBankSubsector &&
                          fileValidationStatus === "valid" && (
                            <motion.p
                              initial={{ opacity: 0, x: -5 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="text-xs font-medium flex items-center gap-1 text-green-500 dark:text-green-400"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Financial statements verified
                            </motion.p>
                          )}
                        {fileValidationStatus === "invalid" && (
                          <motion.p
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-xs font-medium flex items-center gap-1 text-red-500 dark:text-red-400"
                          >
                            <AlertCircle className="w-3 h-3" />
                            File validation failed
                          </motion.p>
                        )}
                      </div>

                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile();
                          }}
                          className={`p-1 rounded-md ${
                            fileValidationStatus === "valid"
                              ? theme === "dark"
                                ? "text-green-300 hover:text-red-400 hover:bg-red-900/20"
                                : "text-green-700 dark:text-green-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                              : theme === "dark"
                                ? "text-red-300 hover:text-red-400 hover:bg-red-900/20"
                                : "text-red-700 dark:text-red-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                          }`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    </motion.div>
                    {sheetData &&
                      sheetData.length > 0 &&
                      fileValidationStatus === "valid" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className={`mt-4 overflow-auto max-h-[70vh] border rounded-lg shadow-md ${
                            theme === "dark"
                              ? "border-gray-700 bg-gray-900"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          <div
                            className={`sticky top-0 z-10 px-3 py-2 border-b text-sm font-semibold ${
                              theme === "dark"
                                ? "bg-gray-800/95 border-gray-700 text-gray-200"
                                : "bg-gray-100/95 border-gray-300 text-gray-700 dark:text-slate-200"
                            }`}
                          >
                            📊 Data Preview (First Sheet)
                          </div>

                          <table className="min-w-full border-collapse text-sm">
                            <tbody>
                              {sheetData.map((row, rowIndex) => (
                                <tr
                                  key={rowIndex}
                                  className={
                                    rowIndex % 2 === 0
                                      ? theme === "dark"
                                        ? "bg-gray-800"
                                        : "bg-gray-50 dark:bg-slate-900"
                                      : ""
                                  }
                                >
                                  {row.map((cell, cellIndex) => (
                                    <td
                                      key={cellIndex}
                                      className={`px-2 py-1 border text-[13px] font-medium truncate ${
                                        theme === "dark"
                                          ? "border-gray-700 text-gray-200"
                                          : "border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white"
                                      }`}
                                    >
                                      {cell ?? ""}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </motion.div>
                      )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.5, type: "spring" }}
                    className="!space-y-6"
                  >
                    {isValidating ? (
                      <motion.div className="!space-y-6">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 2,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "linear",
                          }}
                          className={`!h-20 !w-20 !border-4 !border-t-transparent !rounded-full !mx-auto !shadow-2xl ${theme === "dark" ? "!border-blue-500" : "!border-blue-600"}`}
                        />
                        <motion.p
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{
                            duration: 2,
                            repeat: Number.POSITIVE_INFINITY,
                          }}
                          className={`!text-2xl !font-bold ${theme === "dark" ? "!text-white" : "!text-slate-900 dark:!text-white"}`}
                        >
                          Validating file...
                        </motion.p>
                        {isFiBankSubsector && (
                            <p
                              className={`!text-base !font-semibold ${theme === "dark" ? "!text-slate-400" : "!text-slate-600 dark:!text-slate-400"}`}
                            >
                              Checking required financial statements...
                            </p>
                          )}
                        <p
                          className={`!text-base !font-medium ${theme === "dark" ? "!text-slate-400" : "!text-slate-600 dark:!text-slate-400"}`}
                        >
                          Please wait while we verify your document
                        </p>
                      </motion.div>
                    ) : (
                      <>
                        <motion.div
                          animate={{
                            y: [0, -15, 0],
                            rotate: [0, 5, -5, 0],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                          }}
                        >
                          <div className="!relative !inline-block">
                            <div
                              className={`!p-6 !rounded-3xl !shadow-2xl ${
                                theme === "dark"
                                  ? "!bg-slate-700/50"
                                  : "!bg-slate-100"
                              }`}
                            >
                              <Upload className="!h-20 !w-20 !text-slate-400 dark:!text-slate-500 !mx-auto" />
                            </div>
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{
                                duration: 2,
                                repeat: Number.POSITIVE_INFINITY,
                              }}
                              className="!absolute !-top-3 !-right-3 !w-10 !h-10 !bg-gradient-to-r !from-blue-500 !to-purple-500 !rounded-full !flex !items-center !justify-center !shadow-2xl"
                            >
                              <Sparkles className="!w-5 !h-5 !text-white" />
                            </motion.div>
                          </div>
                        </motion.div>

                        <div className="!space-y-4">
                          <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className={`!text-3xl !font-bold ${theme === "dark" ? "!text-white" : "!text-slate-900 dark:!text-white"}`}
                          >
                            {isDragActive
                              ? "Drop your file here"
                              : "Drag and drop your file"}
                          </motion.p>
                          <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className={`!text-lg !font-semibold ${theme === "dark" ? "!text-slate-400" : "!text-slate-600 dark:!text-slate-400"}`}
                          >
                            or click to browse files
                          </motion.p>
                        </div>
                      </>
                    )}

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className={`!text-sm !space-y-2 !p-6 !rounded-2xl !border-2 !shadow-lg 
                        ${theme === "dark" ? "!text-slate-400 !bg-slate-800/50 !border-slate-700/50" : "!text-slate-500 dark:!text-slate-400 !bg-slate-50/50 !border-slate-200/50"}`}
                    >
                      <p className="!font-bold !text-base">
                        Supported formats: .xlsx, .pdf
                      </p>
                      <p className="!font-medium">Maximum file size: 10MB</p>
                      {isFiBankSubsector && (
                        <div className="!mt-3 !pt-3 !border-t-2 !border-slate-600/30 !space-y-1">
                          <p className="!font-bold !text-base !text-blue-400">
                            Required for Banks & DFIs:
                          </p>
                          <p className="!text-sm !font-medium">
                            Key Financials, Balance Sheet, Income Statement,
                            Ratios, Off Balance Sheet
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {showAdditionalInfo &&
        uploadedFile &&
        fileValidationStatus === "valid" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card
              className={`border-2 shadow-2xl backdrop-blur-sm overflow-hidden ${
                theme === "dark"
                  ? "bg-gray-800/50 border-gray-700"
                  : "bg-white/80 border-gray-200"
              }`}
            >
              <CardContent className="p-8">
                <form onSubmit={onStartAnalysis} className="space-y-6">
                  {(error || validationError) && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <Alert
                        variant="destructive"
                        className="border-2 border-red-500/50 bg-red-500/10 shadow-xl"
                      >
                        <AlertCircle className="h-5 w-5" />
                        <AlertDescription className="text-red-400 font-semibold">
                          {error || validationError}
                        </AlertDescription>
                      </Alert>
                    </motion.div>
                  )}

                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1"
                      >
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => window.location.reload()}
                          disabled={isProcessing}
                          className={cn(
                            "w-full h-12 rounded-xl font-semibold text-base transition-all duration-300 shadow-md border-2",
                            theme === "dark"
                              ? "border-gray-600 text-gray-300 bg-gray-800/60 hover:bg-gray-700/80 hover:text-white hover:border-gray-500 disabled:opacity-50"
                              : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-900 hover:border-gray-400 dark:hover:border-slate-500 disabled:opacity-50",
                          )}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-[2]"
                      >
                        <Button
                          type="submit"
                          disabled={isProcessing || startAnalysisDisabled}
                          className={cn(
                            "w-full h-12 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all duration-300 shadow-xl border-0 relative overflow-hidden group",
                            theme === "dark"
                              ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 disabled:opacity-50"
                              : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white hover:from-indigo-400 hover:via-purple-400 hover:to-pink-400 disabled:opacity-50",
                          )}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/0 dark:from-slate-900/0 via-white/20 dark:via-slate-900/20 to-white/0 dark:to-slate-900/0"
                            animate={{
                              x: ["-100%", "100%"],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                          />

                          {isProcessing ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 1,
                                  repeat: Infinity,
                                  ease: "linear",
                                }}
                              >
                                <Zap className="w-5 h-5" />
                              </motion.div>
                              <span className="relative z-10">
                                Analyzing...
                              </span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5 relative z-10" />
                              <span className="relative z-10">
                                Start AI Analysis
                              </span>
                              <motion.div
                                animate={{ x: [0, 4, 0] }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                }}
                              >
                                <ArrowRight className="w-5 h-5 relative z-10" />
                              </motion.div>
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

      <AnimatePresence>
        {validationError && !uploadedFile && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              variant="destructive"
              className={`!shadow-2xl !shadow-red-500/20 !border-2 !font-semibold ${theme === "dark" ? "!bg-red-950/30 !border-red-800/50 !text-red-200" : "!bg-red-50 dark:!bg-red-900/30 !border-red-200 dark:!border-red-800 !text-red-800 dark:!text-red-200"}`}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <AlertCircle className="!h-6 !w-6" />
              </motion.div>
              <AlertDescription className="ml-8 text-base">
                {validationError}
              </AlertDescription>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>
      <div ref={bottomRef}></div>

      <AnimatePresence>
        {isPdf && uploadedFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-3xl mx-4 p-6 rounded-2xl shadow-2xl border-2 ${
                theme === "dark"
                  ? "bg-gray-900 border-gray-700 text-white"
                  : "bg-white border-gray-200 text-gray-900 dark:text-white"
              }`}
            >
              {/* Close button */}
              {/* <button
                onClick={() => setIsPdf(false)}
                className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "text-gray-400 hover:text-white hover:bg-gray-800"
                    : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100"
                }`}
              >
                <X className="w-5 h-5" />
              </button> */}

              {/* Icon */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-red-500/10">
                  <FileSpreadsheet className="w-6 h-6 text-red-500 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">PDF Detected</h2>
                  <p
                    className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"}`}
                  >
                    {uploadedFile?.name}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                {/* Body — customise this as you build it out */}
                {isPdfProcessing == "processing" && (
                  <p
                    className={`text-sm mb-6 leading-relaxed ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}
                  >
                    "We're processing the uploaded PDF file..."
                  </p>
                )}
                {isPdfProcessing == "processed" && <Table data={pdfPreview} />}
              </div>
              {/* Actions */}
              <div className="flex gap-3">
                {/* <Button
                variant="outline"
                onClick={() => {
                  setIsPdf(false);
                  removeFile();
                }}
                className="flex-1 cursor-pointer"
              >
                <X className="w-4 h-4 mr-2" />
                Remove File
              </Button> */}
                <Button
                  disabled={isPdfProcessing == "processing"}
                  onClick={() => {
                    setIsPdf(false);
                    setPdfValid(true);
                    setPdfData(pdfPreview);
                    if (setAuditorOpinion) setAuditorOpinion(auditorOpinionData);
                  }}
                  className="flex-1 cursor-pointer bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Validate
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
